import { z } from "zod";

import { TaskSource } from "@/src/lib/task-source";
import { Classification } from "@/src/models/constants/classifications";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { Section } from "@/src/models/section";

export enum FileType {
  READY = "ready", // TODO: remove VAL-300
  ORIGINAL = "original", // TODO: remove VAL-300
  INBOUND = "inbound",
  OUTBOUND = "outbound",
}

export enum FilePriority {
  LOW = "Low",
  MEDIUM = "Medium",
  HIGH = "High",
}

export const splitDocumentConfidenceSchema = z.record(
  z.nativeEnum(Classification),
  z.object({
    pages: z.array(z.number()),
    pageRanges: z.array(z.array(z.number())),
    confidence: z.number(),
    reasoning: z.string().nullable(),
  })
);

export type SplitDocumentConfidenceDict = z.infer<typeof splitDocumentConfidenceSchema>;
export type ClassificationPageDict = Partial<Record<Classification, { pages: number[]; pageRanges: number[][] }>>;

const intakeGroupingPageRangeSchema = z
  .object({
    startPage: z.number().int().nonnegative(),
    endPage: z.number().int().nonnegative(),
  })
  .refine(({ startPage, endPage }) => startPage <= endPage);

const intakeGroupingSectionSchema = z.object({
  classification: z.nativeEnum(Classification),
  pageRanges: z.array(intakeGroupingPageRangeSchema).min(1),
  patientIndex: z.number().int().positive(),
  hasMultiplePatients: z.boolean(),
});

export const intakeSectionGroupingSchema = z.object({
  sourceSections: z.array(intakeGroupingSectionSchema).min(1),
  sections: z.array(intakeGroupingSectionSchema.extend({ reasoning: z.string().trim().min(1).nullable() })).min(1),
});

export type IntakeSectionGrouping = z.infer<typeof intakeSectionGroupingSchema>;
export type IntakeSectionGroupingSource = IntakeSectionGrouping["sourceSections"][number];
export type IntakeSectionGroupingSection = IntakeSectionGrouping["sections"][number];

export const fileSchema = z.object({
  pathToFile: z.string(),
  filename: z.string(),
  contentType: z.string(),
  checksum: z.string(),
  classification: z.nativeEnum(Classification).nullable(),
  type: z.nativeEnum(FileType),
  fileSize: z.number(),
  pageCount: z.number().int().nonnegative().nullable(),
  splitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  reviewedSplitDocumentConfidence: splitDocumentConfidenceSchema.nullable(),
  intakeSectionGrouping: intakeSectionGroupingSchema.nullable(),
  reviewedIntakeSectionGrouping: intakeSectionGroupingSchema.nullable(),
  completedAt: z.string().nullable(),
  displayId: z.string().nullable(),
  note: z.string().nullable(),
  priority: z.nativeEnum(FilePriority).default(FilePriority.LOW),
  ehrDocumentId: z.string().nullable(),
  ehrDocumentType: z.string().nullable(),
  source: z.nativeEnum(TaskSource).nullable(),
  pageHashes: z
    .array(z.object({ pageNumber: z.number(), hash: z.string() }))
    .nullable()
    .optional(),
  pathToDeduplicationImages: z.string().nullable().optional(),
  duplicatesFileId: z.string().uuid().nullable().optional(),
  hasMultiplePatients: z.boolean().default(false),
  croppedPageIndices: z.array(z.number().int().nonnegative()).nullable(),
});

export type File = z.infer<typeof fileSchema> & {
  sections: Section[];
} & DatabaseDefaults;

export const fileShape: File = {
  id: "",
  groupId: "",
  pathToFile: "",
  filename: "",
  contentType: "",
  checksum: "",
  type: FileType.READY,
  fileSize: 0,
  pageCount: null,
  classification: null,
  splitDocumentConfidence: null,
  reviewedSplitDocumentConfidence: null,
  intakeSectionGrouping: null,
  reviewedIntakeSectionGrouping: null,
  sections: [],
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
  completedAt: null,
  displayId: null,
  note: null,
  priority: FilePriority.LOW,
  ehrDocumentId: null,
  ehrDocumentType: null,
  source: null,
  pageHashes: null,
  pathToDeduplicationImages: null,
  duplicatesFileId: null,
  hasMultiplePatients: false,
  croppedPageIndices: null,
};
